import React, { useState, useRef, useEffect } from 'react'
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet'
import 'leaflet/dist/leaflet.css'
import L from 'leaflet'
import ShopList from './components/ShopList'
import { useShops } from './hooks/useShops'

// Fix for default markers in react-leaflet
delete L.Icon.Default.prototype._getIconUrl
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
})

// Center coordinates for Bandar Sri Indah
const CENTER_POSITION = [4.2738396,118.0129159]

function App() {
  const [selectedShop, setSelectedShop] = useState(null)
  const [viewMode, setViewMode] = useState('list') // 'list' or 'map' for mobile
  const [mapKey, setMapKey] = useState(0) // Key to force re-render map
  const [darkMode, setDarkMode] = useState(false) // Dark mode state
  const mapRef = useRef(null) // Reference to the map instance
  const { data: shops, isLoading, error } = useShops()

  // Handle shop selection from the list
  const handleShopSelect = (shop) => {
    setSelectedShop(shop)
    // Auto switch to map view on mobile when a shop is selected
    if (window.innerWidth < 768) {
      setViewMode('map')
    }
  }


  // Handle back to list view
  const handleBackToList = () => {
    setViewMode('list')
    setSelectedShop(null)
  }

  // Handle view mode change with map re-render
  const handleViewModeChange = (mode) => {
    if (mode === 'map' && viewMode !== 'map') {
      // Force re-render of map when switching to map view
      setMapKey(prevKey => prevKey + 1)
    }
    setViewMode(mode)
  }

  // Show loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading shops...</p>
        </div>
      </div>
    )
  }

  // Show error state
  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-500">Error loading shops: {error.message}</p>
        </div>
      </div>
    )
  }

  return (
    <div className={`min-h-screen flex flex-col ${darkMode ? 'bg-gray-900 text-white' : 'bg-gray-50 text-gray-900'}`}>
      {/* Header */}
      <header className={`${darkMode ? 'bg-gray-800' : 'bg-white'} shadow-sm`}>
        <div className="max-w-7xl mx-auto px-4 py-3 sm:py-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center">
            {/* Back button for mobile map view */}
            {viewMode === 'map' && window.innerWidth < 768 && (
              <button
                onClick={handleBackToList}
                className={`flex items-center font-medium touch-manipulation ${darkMode ? 'text-blue-400' : 'text-primary'}`}
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd" />
                </svg>
                Back
              </button>
            )}
            
            <div className={viewMode === 'map' && window.innerWidth < 768 ? "text-center flex-1" : ""}>
              <h1 className="text-xl sm:text-2xl font-bold">IndahGo</h1>
              <p className="text-sm sm:text-base opacity-75">Discover shops in Bandar Sri Indah</p>
            </div>
            
            <div className="flex items-center space-x-3">
              {/* Dark mode toggle */}
              <button
                onClick={() => setDarkMode(!darkMode)}
                className={`p-2 rounded-full ${darkMode ? 'bg-yellow-400 text-gray-900' : 'bg-gray-800 text-yellow-400'}`}
                aria-label="Toggle dark mode"
              >
                {darkMode ? (
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 2a1 1 0 011 1v1a1 1 0 11-2 0V3a1 1 0 011-1zm4 8a4 4 0 11-8 0 4 4 0 018 0zm-.464 4.95l.707.707a1 1 0 001.414-1.414l-.707-.707a1 1 0 00-1.414 1.414zm2.12-10.607a1 1 0 010 1.414l-.706.707a1 1 0 11-1.414-1.414l.707-.707a1 1 0 011.414 0zM17 11a1 1 0 100-2h-1a1 1 0 100 2h1zm-7 4a1 1 0 011 1v1a1 1 0 11-2 0v-1a1 1 0 011-1zM5.05 6.464A1 1 0 106.465 5.05l-.708-.707a1 1 0 00-1.414 1.414l.707.707zm1.414 8.486l-.707.707a1 1 0 01-1.414-1.414l.707-.707a1 1 0 011.414 1.414zM4 11a1 1 0 100-2H3a1 1 0 000 2h1z" clipRule="evenodd" />
                  </svg>
                ) : (
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                    <path d="M17.293 13.293A8 8 0 016.707 2.707a8.001 8.001 0 1010.586 10.586z" />
                  </svg>
                )}
              </button>
              
              {/* Mobile view toggle buttons - only show in list view */}
              {viewMode === 'list' && (
                <div className="md:hidden flex space-x-2">
                  <button
                    onClick={() => handleViewModeChange('list')}
                    className={`px-3 py-1 rounded-lg text-sm font-medium touch-manipulation ${viewMode === 'list' ? (darkMode ? 'bg-blue-600 text-white' : 'bg-primary text-white') : (darkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-700')}`}
                  >
                    List
                  </button>
                  <button
                    onClick={() => handleViewModeChange('map')}
                    className={`px-3 py-1 rounded-lg text-sm font-medium touch-manipulation ${viewMode === 'map' ? (darkMode ? 'bg-blue-600 text-white' : 'bg-primary text-white') : (darkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-700')}`}
                  >
                    Map
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Main content */}
      <main className="flex-1 max-w-7xl mx-auto px-4 py-4 sm:px-6 lg:px-8 w-full">
        <div className="flex flex-col md:flex-row gap-4 h-[calc(100vh-120px)] sm:h-[calc(100vh-140px)]">
          {/* Shop List - Hidden on mobile when map view is active */}
          <div className={`${viewMode === 'list' ? 'flex' : 'hidden'} md:flex w-full md:w-1/3 flex-col h-full`}>
            <ShopList shops={shops || []} onShopSelect={handleShopSelect} darkMode={darkMode} />
          </div>
          
          {/* Map - Hidden on mobile when list view is active */}
          <div className={`${viewMode === 'map' ? 'flex' : 'hidden'} md:flex w-full md:w-2/3 ${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow-sm overflow-hidden relative h-full`}>
            {/* Selected shop info bar for mobile */}
            {selectedShop && window.innerWidth < 768 && (
              <div className={`absolute top-0 left-0 right-0 p-3 z-[1000] shadow-sm rounded-b-lg ${darkMode ? 'bg-gray-800' : 'bg-white bg-opacity-95'}`}>
                <h3 className={`font-semibold ${darkMode ? 'text-white' : 'text-gray-800'}`}>{selectedShop.name}</h3>
                <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>{selectedShop.category}</p>
              </div>
            )}
            
            <div className="map-container h-full w-full">
              <MapContainer
                key={mapKey}
                center={selectedShop ? [selectedShop.lat, selectedShop.lng] : CENTER_POSITION}
                zoom={selectedShop ? 16 : 15}
                className="h-full w-full z-0"
                whenCreated={(map) => {
                  // Store the map instance in the ref
                  mapRef.current = map;
                  // Ensure map is properly sized when displayed
                  setTimeout(() => {
                    map.invalidateSize();
                    // Force a resize event to ensure proper rendering
                    window.dispatchEvent(new Event('resize'));
                  }, 300);
                }}
              >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              
              {/* Main location marker */}
              <Marker
                position={CENTER_POSITION}
              >
                <Popup>
                  <div className="p-1">
                    <h3 className="font-semibold text-gray-800">Bandar Sri Indah</h3>
                    <p className="text-sm text-gray-600">Main Location</p>
                    <p className="text-sm text-gray-500 mt-1">Central point of Bandar Sri Indah</p>
                    <button
                      onClick={() => window.open(`https://www.google.com/maps/dir/?api=1&destination=${CENTER_POSITION[0]},${CENTER_POSITION[1]}`, '_blank')}
                      className="mt-2 bg-blue-500 hover:bg-blue-600 text-white text-xs px-2 py-1 rounded"
                    >
                      Get Directions
                    </button>
                  </div>
                </Popup>
              </Marker>
              
              {/* Render markers for each shop */}
              {shops?.map((shop) => (
                <Marker
                  key={shop.id}
                  position={[shop.lat, shop.lng]}
                >
                  <Popup>
                    <div className="p-1">
                      <h3 className="font-semibold text-gray-800">{shop.name}</h3>
                      <p className="text-sm text-gray-600">{shop.category}</p>
                      <p className="text-sm text-gray-500 mt-1">{shop.description}</p>
                      {shop.phone && (
                        <p className="text-sm text-gray-500 mt-1">
                          <span className="font-medium">Phone:</span> {shop.phone}
                        </p>
                      )}
                      {shop.hours && (
                        <p className="text-sm text-gray-500 mt-1">
                          <span className="font-medium">Hours:</span> {shop.hours}
                        </p>
                      )}
                      <button
                        onClick={() => window.open(`https://www.google.com/maps/dir/?api=1&destination=${shop.lat},${shop.lng}`, '_blank')}
                        className="mt-2 bg-blue-500 hover:bg-blue-600 text-white text-xs px-2 py-1 rounded"
                      >
                        Get Directions
                      </button>
                    </div>
                  </Popup>
                </Marker>
              ))}
              </MapContainer>
            </div>
          </div>
        </div>
      </main>
    </div>
  )
}

export default App